import React from "react";

import { FormSectionHeader, getFormSectionStatus } from "../../../../components/FormSectionHeader";
import { GameConceptDisplayMessage } from "../../../../components/GameConceptDisplayMessage/GameConceptDisplayMessage";
import { ReadOnlyFieldList } from "../../../../components/ReadOnlyFieldList";
import {
  aaaSectionFields,
  idXboxSectionFields,
} from "../../../../components/ReadOnlyFieldList/sectionFieldConfigs";
import { RightSidePortalContext } from "../../../../components/RightSideWrapper/RightSidePortalContext";
import { SubFormNavigationProps } from "../../../../components/SubForm";
import { CurrentGameConcept } from "../../../../models/crmStatus";
import { SubFormsRenderingModel } from "../../../../models/sharedFormsModels";
import * as styles from "../../GameConceptPage.module.css";
import { useReadOnlyValueFormatter } from "../../useReadOnlyValueFormatter";

type GameConceptSectionsProps = Readonly<{
  children?: React.ReactNode;
  accountType: "id" | "aaa";
  currentGameConcept: CurrentGameConcept | undefined;
  isReadOnly: boolean;
  subFormsOrder: ReadonlyArray<string>;
  subFormsList: ReadonlyDictionary<SubFormsRenderingModel>;
  openSections: ReadonlySet<number>;
  startedSections: ReadonlySet<string>;
  sectionValidity: Readonly<Record<string, boolean>>;
  formData: Record<string, Record<string, unknown>> | null | undefined;
  getSubFormProps: (subFormId: string) => SubFormNavigationProps;
  onSectionClick: (index: number) => void;
}>;

export const GameConceptSections = ({
  children,
  accountType,
  currentGameConcept,
  isReadOnly,
  subFormsOrder,
  subFormsList,
  openSections,
  startedSections,
  sectionValidity,
  formData,
  getSubFormProps,
  onSectionClick,
}: GameConceptSectionsProps) => {
  const sectionFields = accountType === "aaa" ? aaaSectionFields : idXboxSectionFields;
  const formatReadOnlySectionData = useReadOnlyValueFormatter();

  return (
    <div className={styles.mainColumn}>
      {children}
      <GameConceptDisplayMessage currentGameConcept={currentGameConcept} />
      {isReadOnly
        ? subFormsOrder.map((subFormId, index) => {
            const subFormModel = subFormsList[subFormId];
            const isOpen = openSections.has(index);
            const sectionData = formData?.[subFormId] as Record<string, unknown> | undefined;
            const fields = sectionFields[subFormId] ?? [];
            const headerId = `game-concept-header-${subFormId}`;
            const panelId = `game-concept-panel-${subFormId}`;
            return (
              <div key={subFormId} className={styles.accordionSection}>
                <button
                  className={`${styles.accordionHeader} ${
                    isOpen ? styles.accordionHeaderOpen : ""
                  }`}
                  onClick={() => onSectionClick(index)}
                  type="button"
                  id={headerId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className={styles.accordionTitle}>{subFormModel.title}</span>
                  <span
                    aria-hidden="true"
                    className={`${styles.accordionChevron} ${
                      isOpen ? styles.accordionChevronOpen : ""
                    }`}
                  >
                    &#8250;
                  </span>
                </button>
                {isOpen && (
                  <div id={panelId} role="region" aria-labelledby={headerId}>
                    <ReadOnlyFieldList
                      data={formatReadOnlySectionData(sectionData)}
                      fields={fields}
                    />
                  </div>
                )}
              </div>
            );
          })
        : subFormsOrder.map((subFormId, index) => {
            const subFormModel = subFormsList[subFormId];
            const isOpen = openSections.has(index);
            const tooltipTargetId = `game-concept-tooltip-${subFormId}`;
            const headerId = `game-concept-header-${subFormId}`;
            const panelId = `game-concept-panel-${subFormId}`;
            return (
              <div key={subFormId} className={styles.accordionSection}>
                <button
                  className={`${styles.accordionHeader} ${
                    isOpen ? styles.accordionHeaderOpen : ""
                  }`}
                  onClick={() => onSectionClick(index)}
                  type="button"
                  id={headerId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <FormSectionHeader
                    status={getFormSectionStatus(
                      startedSections.has(subFormId),
                      sectionValidity[subFormId] === true
                    )}
                    title={subFormModel.title}
                  />
                  <span
                    aria-hidden="true"
                    className={`${styles.accordionChevron} ${
                      isOpen ? styles.accordionChevronOpen : ""
                    }`}
                  >
                    &#8250;
                  </span>
                </button>
                {isOpen && (
                  <div
                    id={panelId}
                    className={styles.accordionContent}
                    role="region"
                    aria-labelledby={headerId}
                  >
                    <div id={tooltipTargetId} />
                    <RightSidePortalContext.Provider value={tooltipTargetId}>
                      {subFormModel.renderSubForm(getSubFormProps(subFormId))}
                    </RightSidePortalContext.Provider>
                  </div>
                )}
              </div>
            );
          })}
    </div>
  );
};
